import React, { useEffect, useState, useCallback, useMemo } from 'react'
import GradientBlob from '../../common/GradientBlob'
import { useMotionPreferences } from '../../../context/MotionPreferencesContext'
import styles from './Hero.module.css'

// Rotating tagline pairs — decorative sub-headline
const TAGLINES = [
  { line1: 'Legacy to scale.', line2: 'AI-native systems.' },
  { line1: 'Fragile to resilient.', line2: 'Intelligent platforms.' },
  { line1: 'Yesterday\u2019s tech.', line2: 'Tomorrow\u2019s AI.' },
  { line1: 'Complex systems.', line2: 'Clean product engines.' },
  { line1: 'Escape legacy drag.', line2: 'Ship AI features faster.' },
  { line1: 'Cloud-native systems.', line2: 'That scale with you.' },
  { line1: 'Enterprise reliability.', line2: 'AI-powered innovation.' },
  { line1: 'Scalable architecture.', line2: 'AI-first by design.' },
]

const Hero = () => {
  const { reducedMotion } = useMotionPreferences()
  const [showLine1, setShowLine1] = useState(false)
  const [showLine2, setShowLine2] = useState(false)
  const [showSubtitle, setShowSubtitle] = useState(false)
  const [showMeta, setShowMeta] = useState(false)
  const [showButtons, setShowButtons] = useState(false)
  const [showSocial, setShowSocial] = useState(false)
  const [taglineIndex, setTaglineIndex] = useState(() => Math.floor(Math.random() * TAGLINES.length))
  const [isTaglineFading, setIsTaglineFading] = useState(false)

  const currentTagline = useMemo(() => TAGLINES[taglineIndex], [taglineIndex])

  const rotateTagline = useCallback(() => {
    setIsTaglineFading(true)
    setTimeout(() => {
      setTaglineIndex((prev) => (prev + 1) % TAGLINES.length)
      setIsTaglineFading(false)
    }, 400)
  }, [])

  useEffect(() => {
    if (reducedMotion) {
      setShowLine1(true)
      setShowLine2(true)
      setShowSubtitle(true)
      setShowMeta(true)
      setShowButtons(true)
      setShowSocial(true)
      return
    }

    const timers = [
      setTimeout(() => setShowLine1(true), 200),
      setTimeout(() => setShowLine2(true), 600),
      setTimeout(() => setShowSubtitle(true), 1100),
      setTimeout(() => setShowMeta(true), 1400),
      setTimeout(() => setShowButtons(true), 1600),
      setTimeout(() => setShowSocial(true), 1900),
    ]

    return () => timers.forEach(clearTimeout)
  }, [reducedMotion])

  useEffect(() => {
    if (reducedMotion) return
    const interval = setInterval(rotateTagline, 6000)
    return () => clearInterval(interval)
  }, [reducedMotion, rotateTagline])

  return (
    <section className={styles.hero} id="home">
      <GradientBlob className={styles.blobBackground} />

      <div className={styles.container}>
        <div className={styles.content}>
          {/* SEO H1 - keyword-rich, screen-reader only so headline stays punchy */}
          <h1 className={styles.seoTitle}>
            Inon Baguio — Principal Software Engineer &amp; Solutions Architect Consultant
          </h1>

          {/* Display headline - rotating taglines */}
          <div className={styles.taglineWrapper} aria-hidden="true">
            <span className={`${styles.titleLine} ${showLine1 ? styles.visible : ''} ${isTaglineFading ? styles.fading : ''}`}>
              {currentTagline.line1}
            </span>
            <span className={`${styles.titleLine} ${styles.gradientText} ${showLine2 ? styles.visible : ''} ${isTaglineFading ? styles.fading : ''}`}>
              {currentTagline.line2}
            </span>
          </div>

          {/* Subtitle */}
          <p className={`${styles.subtitle} ${showSubtitle ? styles.visible : ''}`}>
            I&rsquo;m <strong>Inon Baguio</strong>, a Principal Engineer &amp; Solutions Architect helping software companies modernize legacy systems, ship AI features, and scale cloud infrastructure.
            <span className={styles.subtitleHighlight}>12+ years of proven results — Canada-based, consulting worldwide.</span>
          </p>

          {/* Meta row */}
          <div className={`${styles.metaRow} ${showMeta ? styles.visible : ''}`} aria-hidden="true">
            <span className={styles.metaItem}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Victoria, BC
            </span>
            <span className={styles.metaDivider} />
            <span className={styles.metaItem}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Replies within 24h
            </span>
            <span className={styles.metaDivider} />
            <span className={styles.metaItem}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              PHP &middot; Node &middot; AWS &middot; LLMs
            </span>
          </div>

          {/* CTAs */}
          <div className={`${styles.actions} ${showButtons ? styles.visible : ''}`}>
            <a
              href="https://calendly.com/inonbaguio/30min"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.primaryBtn}
              data-cursor-hover
            >
              <span className={styles.btnText}>Book a 30-min intro</span>
              <span className={styles.btnIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </a>
            <a
              href="#projects"
              className={styles.secondaryBtn}
              data-cursor-hover
            >
              View selected work
            </a>
          </div>

          {/* Social */}
          <div className={`${styles.socialLinks} ${showSocial ? styles.visible : ''}`}>
            {[
              { href: 'https://github.com/inonbaguio', label: 'GitHub', icon: 'M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z' },
              { href: 'https://linkedin.com/in/inonbaguio', label: 'LinkedIn', icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
              { href: 'https://medium.com/@inonbaguio_2091', label: 'Medium', icon: 'M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z' },
              { href: 'mailto:inonbaguio@gmail.com', label: 'Email', icon: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z', external: false },
            ].map((social, index) => (
              <a
                key={social.label}
                href={social.href}
                target={social.external !== false ? '_blank' : undefined}
                rel={social.external !== false ? 'noopener noreferrer' : undefined}
                className={styles.socialLink}
                aria-label={social.label}
                data-cursor-hover
                style={{ '--index': index }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d={social.icon} />
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className={`${styles.scrollIndicator} ${showSocial ? styles.visible : ''}`} aria-hidden="true">
        <div className={styles.scrollMouse}>
          <div className={styles.scrollWheel} />
        </div>
        <span>Scroll</span>
      </div>
    </section>
  )
}

export default Hero
